"use client";

import { useCallback, useEffect, useState } from "react";
import { useModal } from "@/components/providers/modal-provider";
import { modalIds } from "@/components/modals/constants";
import {
  DEFAULT_STATE,
  QUIZ_STATE_CHANGE,
  type QuizPersistentState,
} from "../constants";
import { getQuizState, setQuizState } from "../utils/utils";
import { sendEvent, EventNames } from "@/lib/utils/analytics";
import { type TouchpointType } from "@/lib/quiz-attribution";

/**
 * Central reactive hook for quiz persistent state.
 *
 * Subscribes to a custom DOM event dispatched by setQuizState/resetQuizState,
 * so every component using this hook stays in sync across the tree without
 * needing a shared React Context.
 *
 * Note: QuizRenderer deliberately uses getQuizState/setQuizState directly
 * (not this hook) because it needs the persisted question index at useState
 * initialisation time, before any effect can fire.
 */
export function useQuizState() {
  const { handleOpenModal } = useModal();

  const [persistedState, setPersistedState] =
    useState<QuizPersistentState>(DEFAULT_STATE);

  useEffect(() => {
    setPersistedState(getQuizState());

    const sync = () => setPersistedState(getQuizState());
    window.addEventListener(QUIZ_STATE_CHANGE, sync);
    return () => window.removeEventListener(QUIZ_STATE_CHANGE, sync);
  }, []);

  const isResuming = persistedState.status === 1;

  const startQuiz = useCallback(() => {
    const current = getQuizState();
    if (current.status === 2) return;
    setQuizState({ status: 1, startedAt: Date.now(), lastQuestion: 0 });
  }, []);

  const advanceQuestion = useCallback((questionIndex: number) => {
    setQuizState({ lastQuestion: questionIndex });
  }, []);

  const completeQuiz = useCallback(() => {
    setQuizState({ status: 2, completedAt: Date.now() });
  }, []);

  const handleQuizTap = useCallback(
    (touchpointType?: TouchpointType) => {
      const state = getQuizState();

      const scrollDepth =
        document.documentElement.scrollHeight > window.innerHeight
          ? Math.round(
              (window.scrollY /
                (document.documentElement.scrollHeight - window.innerHeight)) *
                100,
            )
          : 0;

      if (touchpointType) {
        sendEvent(EventNames.QUIZ_TOUCHPOINT_CLICKED, {
          quiz_source: "bottom_sheet",
          touchpoint_type: touchpointType,
          scroll_depth: scrollDepth,
          page_url: window.location.href,
          user_state: state.status,
        });
      }

      handleOpenModal(modalIds.quiz, { touchpointType });
    },
    [handleOpenModal],
  );

  const ctaLabel =
    persistedState.status === 2
      ? "See Quiz Results"
      : persistedState.status === 1
        ? "Resume Quiz"
        : "Take Quiz";

  return {
    quizState: persistedState,
    isResuming,
    ctaLabel,
    startQuiz,
    advanceQuestion,
    completeQuiz,
    handleQuizTap,
  };
}
